import React, { useEffect, useState } from 'react'
import api from '../../../../../services/api'
import Form, { Input, Select } from '../../../../../components/Form'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import {
  apiCreatePlanFrequency,
  apiListMenuFoodInstitutions,
  apiUpdatePlanFrequency
} from '../../domain/api'
import {
  PAYAMENT_FORM_PERIODICITIES,
  PLAN_FREQUENCIES
} from 'commons/constants/commercial'
import {
  convertValueMaskInNumberWithTwoZero,
  currencyMask,
  genericMaskWithTwoZero
} from 'utlis/mask'
import { IconRemove } from './styles'

type IsOpenInModalProps = {
  idParent: number
  handleOnClose: () => void
}
export interface IPlanFrequencyDefaultValues {
  id: number
  frequency: string
  periodicity: TPeriodicity
  is_active: boolean
  value: string
  institution_id: number
}

type TypesFormProps = {
  isOpenInModal?: false | IsOpenInModalProps
  initialValues?: IPlanFrequencyDefaultValues[]
  typeForm: 'create' | 'update'
  menu_food: { id: number; name: string }
  setMenuFood: React.Dispatch<React.SetStateAction<IMenuFoodData>>
}

export interface ISelectFrequencyInstitution {
  id: number
  institution_id: number
  value: string
  deleted: boolean
}

export const FormPlanFrequency = ({
  isOpenInModal,
  initialValues,
  typeForm,
  menu_food
}: TypesFormProps): JSX.Element => {
  const { addToast } = useToast()
  const [defaultValues, setDefaultValues] = useState<any>()
  const { updateDataTable } = useUpdateDataTable()

  const [selectFormsInstitutions, setSelectFormInstituions] = useState<
    ISelectFrequencyInstitution[]
  >([
    {
      id: undefined,
      institution_id: undefined,
      value: undefined,
      deleted: false
    }
  ])
  const [allInstitutions, setAllInstitutions] = useState<
    IMenuFoodInstitution['institution'][]
  >([])
  const [institutionsLoaded, setInstitutionsLoaded] = useState(false)

  useEffect(() => {
    if (!menu_food.id) return

    setInstitutionsLoaded(false)

    const loadAllInstitutions = async () => {
      try {
        const res = await api.get<IMenuFoodInstitution[]>(
          apiListMenuFoodInstitutions(String(menu_food.id))
        )
        setAllInstitutions(res.data.map(a => a.institution))
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao carregar as instituições',
          description:
            'Ocorreu um erro ao carregar as instituições, por favor, tente novamente.'
        })
      } finally {
        setInstitutionsLoaded(true)
      }
    }
    loadAllInstitutions()
  }, [addToast, menu_food.id])

  const removeOneInstitution = (index: number) => {
    setSelectFormInstituions(old => {
      const b = old[index]
      if (b) {
        b.deleted = true
      }
      return [...old]
    })
  }

  const addInstitutionForm = () => {
    setSelectFormInstituions(old => [
      ...old,
      {
        id: undefined,
        institution_id: undefined,
        value: '',
        deleted: false
      }
    ])
  }

  const updateSelectFormsInstitutions = (
    index: number,
    field: 'value' | 'institution_id',
    newValue: any
  ) => {
    setSelectFormInstituions(old => {
      const newSelectFormInstitution = JSON.parse(JSON.stringify(old))
      const selected = newSelectFormInstitution[index]

      ;(selected as any)[field] = newValue

      return newSelectFormInstitution
    })
  }

  useEffect(() => {
    if (!Array.isArray(initialValues) || !initialValues.length) return
    if (typeForm === 'update' && !institutionsLoaded) return

    const activeIds = new Set(allInstitutions.map(i => Number(i.id)))

    const firstFrequency = {
      frequency: initialValues[0].frequency,
      periodicity: initialValues[0].periodicity,
      is_active: !!initialValues[0].is_active
    }

    setSelectFormInstituions(
      initialValues
        .filter(i => activeIds.has(Number(i.institution_id)))
        .map(i => ({
          id: i.id,
          institution_id: Number(i.institution_id),
          value: currencyMask(String(i.value)).substring(3),
          deleted: false
        }))
    )

    setDefaultValues({
      ...firstFrequency
    })
  }, [initialValues, allInstitutions, institutionsLoaded, typeForm])

  const { activeLoading, disableLoading } = useLoading()

  const onSubmitForm = async (
    dataForm: Omit<IPlanFrequencyDefaultValues, 'planName'>
  ) => {
    if (!isOpenInModal) return

    const data = {
      plans_frequencies: selectFormsInstitutions
        .filter(i => !i.deleted)
        .map(i => ({
          id: i.id,
          menu_food_id: menu_food.id,
          is_active: dataForm.is_active,
          frequency: dataForm.frequency,
          payament_form: JSON.stringify({
            periodicity: dataForm.periodicity,
            value: convertValueMaskInNumberWithTwoZero(i.value)
          }),
          institution_id: i.institution_id
        }))
    }
    try {
      if (typeForm === 'create') {
        const { handleOnClose } = isOpenInModal
        const dataCreate = {
          ...data
        }
        activeLoading()

        try {
          await api.post<IPlanFrequency>(apiCreatePlanFrequency(), dataCreate)
          handleOnClose()
          window.location.reload()
          updateDataTable()
          disableLoading()
          addToast({
            type: 'success',
            title: 'Registro criado',
            description: 'Registro criado com sucesso'
          })
        } catch (error: any) {
          console.log(error.response.data)

          addToast({
            type: 'error',
            title: 'Erro ao adicionar o registro',
            description:
              'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
          })
          handleOnClose()
          updateDataTable()
          disableLoading()
        }
      } else {
        const { handleOnClose } = isOpenInModal
        const dataUpdate = {
          ...data
        }

        try {
          activeLoading()
          await api.put<IPlanFrequency>(apiUpdatePlanFrequency(), dataUpdate)

          disableLoading()
          handleOnClose()
          window.location.reload()
          addToast({
            type: 'success',
            title: 'Registro atualizado',
            description: 'Registro alterado com sucesso'
          })
        } catch (err: any) {
          disableLoading()
          handleOnClose()
          addToast({
            type: 'error',
            title: 'Erro ao atualizar o registro',
            description:
              'Ocorreu um erro ao fazer a atualização, por favor, tente novamente.'
          })
        }
      }
      disableLoading()
    } catch (err) {
      if (typeForm === 'create') {
        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description:
            'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
        })
        if (isOpenInModal) isOpenInModal.handleOnClose()
      }
    }
  }
  return (
    <Form
      onSubmit={onSubmitForm}
      setReset
      defaultValues={{
        ...defaultValues
      }}
    >
      <div className="card">
        <div className="pe-4 ps-4 row">
          <Select
            className={'col-md-6'}
            name="frequency"
            label="Frequência"
            options={PLAN_FREQUENCIES.map(frequency => ({
              name: frequency.name,
              value: frequency.value
            }))}
            rules={{ required: true }}
            blank
          />
          <Select
            className={'col-md-6'}
            name="periodicity"
            label="Periodicidade de contratação"
            options={PAYAMENT_FORM_PERIODICITIES.map(periodicity => ({
              name: periodicity,
              value: periodicity
            }))}
            rules={{ required: true }}
            blank
          />
          {typeForm === 'update' && (
            <Select
              className="col-md-6"
              name="is_active"
              label="Ativo"
              options={[
                {
                  name: 'Sim',
                  value: 'true'
                },
                {
                  name: 'Não',
                  value: 'false'
                }
              ]}
              blank
              defaultValue={'true'}
            />
          )}

          <div className="row gap-5">
            <div className="col-md-6">
              <label className="col-form-label fw-bold fs-6">Instituição</label>
            </div>
            <div className="col-md-2">
              <label className="col-form-label fw-bold fs-6">Valor</label>
            </div>
          </div>

          {selectFormsInstitutions.map(
            ({ id, institution_id, value, deleted }, index) => {
              const normalizedInstitutionId = institution_id
                ? Number(institution_id)
                : undefined

              return (
                !deleted && (
                  <div
                    className="d-flex align-items-center gap-5"
                    key={id ?? `new-${index}`}
                  >
                    <Select
                      className=" col-md-6"
                      name={`institutions_id.${index}.institution_id`}
                      options={(() => {
                        const selectedIds = selectFormsInstitutions
                          .filter(item => !item.deleted)
                          .map(item => Number(item.institution_id))
                          .filter(Boolean)

                        return allInstitutions
                          .filter(
                            item =>
                              !selectedIds.includes(Number(item.id)) ||
                              Number(item.id) === normalizedInstitutionId
                          )
                          .map(institution => ({
                            name: institution.company_social_name,
                            value: institution.id
                          }))
                      })()}
                      defaultValue={''}
                      blank
                      value={normalizedInstitutionId}
                      onChange={event => {
                        updateSelectFormsInstitutions(
                          index,
                          'institution_id',
                          Number(event.target.value)
                        )
                      }}
                      controlled
                    />
                    <Input
                      name={`institutions_id.${index}.value`}
                      className={'col-md-2'}
                      controlled
                      onChange={({ target }) => {
                        updateSelectFormsInstitutions(
                          index,
                          'value',
                          genericMaskWithTwoZero(String(target.value)) || '0'
                        )
                      }}
                      value={value}
                    />
                    {selectFormsInstitutions.filter(
                      selected => !selected.deleted
                    ).length > 1 && (
                      <div className="">
                        <IconRemove
                          onClick={() => removeOneInstitution(index)}
                        />
                      </div>
                    )}
                  </div>
                )
              )
            }
          )}
          <div className="separator my-5" />
          <div className="row">
            <button
              type="button"
              className="btn btn-dark btn-sm mb-5 col-md-2"
              onClick={() => addInstitutionForm()}
            >
              + Instituição
            </button>
          </div>
        </div>
        <div className="card-footer d-flex justify-content-end py-6 px-9">
          <button type="submit" className="btn btn-primary">
            Salvar
          </button>
        </div>
      </div>
    </Form>
  )
}
